// Tabler JS — initialises dropdowns, popovers, and other interactive components.
// This also auto-initialises Bootstrap Tooltips for all [data-bs-toggle="tooltip"] elements.
import '@tabler/core';

// Project SCSS — variable overrides + full Tabler CSS source import.
// The Tabler Icons webfont is loaded via CDN <link> in _Layout.cshtml to avoid
// Vite lib mode inlining 435 KB of font binary as base64 in the CSS bundle.
import './scss/index.scss';

import { HelloWorld } from './elements/hello-world';

// Org portal modules
import { initOrgUsersList } from './org-portal/org-users-list';
import { initUserPortalUsersList } from './org-portal/user-portal-users-list';
import { initUserListExport } from './shared/user-list-export';
import { initOrgApplicationsList } from './org-portal/org-applications-list';
import { initReviewDisbursementModal } from './org-portal/review-disbursement-modal';
import { initResolveDonationModal } from './org-portal/resolve-donation-modal';
import { initManualLedgerEntryModal } from './org-portal/manual-ledger-entry-modal';
import { initAccountNumberReveal } from './org-portal/account-number-reveal';

// School management modules
import { initSchoolsList } from './schools/schools-list';
import { initSchoolUsersList } from './schools/school-users-list';
import { initSchoolApplicationsList } from './schools/school-applications-list';
import { initAddApplication } from './shared/add-application';
import { initGuardianAddressToggle } from './shared/guardian-address-toggle';
import { initReviewApplication } from './shared/review-application';
import { initDisbursementsList } from './shared/disbursements-list';
import { initDonationsList } from './user-portal/donations-list';
import { initSlugPreview } from './schools/slug-preview';
import './shared/dirty-form-guard';
import { initPasswordToggles } from './shared/password-toggle';
import { initPhoneInputs } from './shared/phone-input';
import { initCopyButtons } from './shared/copy-to-clipboard';

// aspnet-validation — jQuery-free replacement for jquery-validation-unobtrusive.
// Reads data-val-* attributes emitted by ASP.NET Core Tag Helpers and populates
// <span asp-validation-for> elements on form submit and blur, activating inline
// validation globally across all forms without a per-view @section Scripts block.
import 'aspnet-client-validation';

// Auto-initialise page modules. main.js is loaded with type="module" defer so the
// DOM is always ready here. Each init function is a no-op when its required
// elements are absent, so it is safe to call on every page.
initSchoolsList();
initSchoolUsersList();
initSchoolApplicationsList();
initAddApplication();
initGuardianAddressToggle();
initReviewApplication();
initSlugPreview('[data-slug-source="true"]', '[data-slug-target="true"]');
initPasswordToggles();
initPhoneInputs();
initCopyButtons();
initOrgUsersList();
initUserPortalUsersList();
initUserListExport();
initOrgApplicationsList();
initReviewDisbursementModal();
initResolveDonationModal();
initManualLedgerEntryModal();
initAccountNumberReveal();
initDisbursementsList();
initDonationsList();

// TipTap editor — loaded lazily when [data-tiptap] is detected.
// Dynamic import keeps TipTap out of the main bundle (zero cost on all other pages).
if (document.querySelector('[data-tiptap]')) {
    import('./settings/landing-page-editor')
        .then(({ initTiptapEditors }) => initTiptapEditors())
        .catch(err => console.error('[TipTap] Failed to load editor:', err));
}

// Tax letter editor — loaded lazily when [data-tiptap-tax-letter] is detected.
const taxLetterWrapper = document.querySelector<HTMLElement>('[data-tiptap-tax-letter]');
if (taxLetterWrapper) {
    import('./settings/tax-letter-editor')
        .then(({ initTaxLetterEditor }) => initTaxLetterEditor(taxLetterWrapper))
        .catch(err => console.error('[TaxLetterEditor] Failed to load editor:', err));
}

// Tempus Dominus date/datetime pickers — loaded lazily when [data-date-picker] is detected.
if (document.querySelector('[data-date-picker]')) {
    import('./shared/date-picker')
        .then(({ initDatePickers }) => initDatePickers())
        .catch(err => console.error('[DatePicker] Failed to load:', err));
}

// Disbursement application picker — loaded lazily when [data-disbursement-picker] is detected.
// Drives the multi-select inline checkbox table on the disbursement request Create pages.
// Configuration is read from data attributes; no inline <script> blocks needed in Razor views.
const disbursementPickerEl = document.querySelector<HTMLElement>('[data-disbursement-picker]');
if (disbursementPickerEl) {
    import('./shared/disbursement-application-picker')
        .then(({ initFromElement }) => initFromElement(disbursementPickerEl))
        .catch(err => console.error('[DisbursementPicker] Failed to load:', err));
}

// Donor picker — loaded lazily when [data-donor-picker] is detected (Org Portal Merge Donation page).
const donorPickerEl = document.querySelector<HTMLElement>('[data-donor-picker]');
if (donorPickerEl) {
    import('./org-portal/donations/donor-picker')
        .then(({ initFromElement }) => initFromElement(donorPickerEl))
        .catch(err => console.error('[DonorPicker] Failed to load:', err));
}

// Currency input — loaded lazily when [data-currency-input] is detected.
// CurrencyTagHelper injects data-currency-input on [Currency]-decorated int inputs.
if (document.querySelector('[data-currency-input]')) {
    import('./shared/currency-input')
        .then(({ initCurrencyInputs }) => initCurrencyInputs())
        .catch(err => console.error('[CurrencyInput] Failed to load:', err));
}

// Donation form — loaded lazily when [data-donation-form] is detected (User Portal).
if (document.querySelector('[data-donation-form]')) {
    import('./user-portal/donation-form')
        .then(({ initDonationForm }) => initDonationForm())
        .catch(err => console.error('[DonationForm] Failed to load:', err));
}

// Org portal donation form — loaded lazily when [data-org-donation-form] is detected.
if (document.querySelector('[data-org-donation-form]')) {
    import('./org-portal/donation-form')
        .then(({ initDonationForm }) => initDonationForm())
        .catch(err => console.error('[OrgDonationForm] Failed to load:', err));
}

// Donations export modal — loaded lazily when [data-donations-export-btn] is detected.
// Shared between Org Portal (date range + optional school filter) and School Portal
// (date range only, always scoped to the current school).
if (document.querySelector('[data-donations-export-btn]')) {
    import('./shared/donations-export')
        .then(({ initDonationsExport }) => initDonationsExport())
        .catch(err => console.error('[DonationsExport] Failed to load:', err));
}

// Table dropdown overflow fix — loaded lazily when a dropdown exists inside a
// .table-responsive container. Lifts overflow while a Bootstrap dropdown is open
// so the menu overlays adjacent content instead of being clipped by the table scroll container.
if (document.querySelector('.table-responsive .dropdown')) {
    import('./shared/table-dropdown-overflow')
        .then(({ initTableDropdownOverflow }) => initTableDropdownOverflow())
        .catch(err => console.error('[TableDropdownOverflow] Failed to load:', err));
}

export { HelloWorld, initSchoolsList, initSchoolUsersList, initSchoolApplicationsList, initAddApplication, initReviewApplication, initDisbursementsList, initDonationsList, initSlugPreview, initPasswordToggles, initOrgUsersList, initUserPortalUsersList, initOrgApplicationsList, initReviewDisbursementModal, initResolveDonationModal, initManualLedgerEntryModal, initAccountNumberReveal, initPhoneInputs, initCopyButtons };
